import { useState, useEffect, Suspense } from "react";
import { PageLoader } from "@/components/PageLoader";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import CommandPalette from "@/components/CommandPalette";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import OnboardingModal from "@/components/OnboardingModal";
import InstallPrompt from "@/components/InstallPrompt";
import AIAssistant from "@/components/AIAssistant";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";
import { Portal } from "@/components/ui/portal";

export default function AppLayout() {
  const { user, loading } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(true);
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Prevent body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Yükleniyor...
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Venni firma temsilcisi hiçbir AppLayout sayfasını görmez — sadece portal
  if (user.role === "venni_supplier") return <Navigate to="/venni-portal" replace />;

  // Engineer (Hasan) ve legal advisor için onboarding gereksiz — modül scope'ları çok dar.
  const isRestrictedRole = user.role === "engineer" || user.role === "legal";
  const showOnboarding = !user.must_change_password && !user.onboarding_done && onboardingOpen && !isRestrictedRole;

  return (
    <div className="flex min-h-screen bg-background">
      {/* ===== Mobile top bar — glass ===== */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-4 py-2.5 glass-strong border-b border-border/60"
        style={{ paddingTop: "max(0.625rem, env(safe-area-inset-top))" }}
      >
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-btn"
          className="p-2 -ml-2 rounded-xl active:bg-muted apple-ease transition-colors touch-target"
          aria-label="Menü"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <img
            src="/logo.png"
            alt="AKB"
            className="h-6 w-auto dark:brightness-0 dark:invert"
            onError={(e) => (e.target.style.display = "none")}
          />
          <span className="font-semibold text-[15px] tracking-tight">AKB Karavan</span>
        </div>
        <div className="w-9" />
      </div>

      {/* ===== Mobile drawer — slide-over ===== */}
      {mobileOpen && (
        <Portal>
        <div className="lg:hidden fixed inset-0 z-40 flex animate-apple-fade" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="relative w-72 max-w-[85vw] shadow-floating animate-apple-slide-up"
            style={{ animation: "apple-slide-up 280ms cubic-bezier(0.32, 0.72, 0, 1) both" }}
          >
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 z-10 p-2 rounded-full bg-muted/60 hover:bg-muted apple-ease touch-target"
              aria-label="Kapat"
              data-testid="mobile-menu-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
            <Sidebar collapsed={false} onToggle={() => setMobileOpen(false)} isMobile />
          </div>
        </div>
        </Portal>
      )}

      {/* ===== Desktop sidebar ===== */}
      <div className="hidden lg:flex">
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
      </div>

      <main
        className="flex-1 min-w-0 overflow-x-hidden pt-14 lg:pt-0"
        style={{ paddingBottom: "max(0px, env(safe-area-inset-bottom))" }}
      >
        <div className="animate-apple-fade">
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      <CommandPalette />
      <ChangePasswordModal open={!!user?.must_change_password} forced onClose={() => {}} />
      <OnboardingModal open={showOnboarding} onClose={() => setOnboardingOpen(false)} />
      <InstallPrompt />
      <AIAssistant user={user} />
    </div>
  );
}
